<template>
  <div
    class="am3-event-card"
    :data-test-id="dataTestId"
    role="article"
    :aria-label="`${props.eventGroup ? 'Event group' : 'Event'}: ${props.eventGroup ? props.eventGroup.name : props.event.name}`"
  >
    <div
      class="relative group/card flex h-full w-full flex-col overflow-hidden rounded-md bg-surface-light border focus-within:shadow-tinted hover:shadow-tinted border-solid border-weak duration-75 transition-shadow cursor-pointer"
      role="presentation"
    >
      <!-- Event Image -->
      <NuxtLink
        :to="{ path: eventLink }"
        class="block aspect-video h-full text-inherit max-h-[180px] no-underline"
        :aria-label="`View ${props.eventGroup ? 'group' : 'event'}: ${props.eventGroup ? props.eventGroup.name : props.event.name}`"
        role="link"
        @click="handleEventNavigation"
      >
        <div
          class="relative aspect-video w-full z-10 flex-shrink-0  max-h-[180px] rounded-t-md overflow-hidden"
          role="img"
          :aria-label="`${props.eventGroup ? 'Group' : 'Event'} image for ${props.eventGroup ? props.eventGroup.name : props.event.name}`"
        >
          <img
            v-if="eventImage"
            class="w-aspect-square object-center"
            :src="eventImage"
            :alt="`Image to represent the event ${props.eventGroup ? props.eventGroup.name : props.event.name}'' `"
            loading="lazy"
          >
          <img
            v-else
            class="h-full w-full bg-primary"
            :src="eventImagePlaceholder"
            alt="placeholder for event - no image defined"
            loading="lazy"
            height="178"
            width="319"
          >

          <!-- Integration Icon -->

          <div
            v-if="integration && integration.icon"
            class="absolute bottom-5 right-5 z-30 size-6 flex flex-col items-center justify-center p-1 bg-surface-light rounded-full shadow-sm"
            role="status"
            :aria-label="`Event was added via ${integration.name} integration`"
            data-test-id="integration-icon"
          >
            <span class="sr-only">event was added via {{ integration.name }} integration </span>
            <AM3Icon
              class="size-full"
              :type="integration.icon"
              :size="'sm'"
              aria-hidden="true"
            />
          </div>

        </div>

      </NuxtLink>

      <!-- controls + pseudo element -->
      <div
        class="top-0 text-inherit aspect-video absolute inset-x-0 z-30 flex items-start flex-row-reverse justify-between after:bg-black after:bg-linear-to-b pointer-events-none after:from-black after:to-transparent after after:size-full after:opacity-0 group-hover/card:after:opacity-30 after:absolute inset-0 after:aspect-video"
        role="toolbar"
        aria-label="Card actions"
      >
        <!-- Selection Checkbox -->
        <div
          v-if="props.selectable"
          class="transition-opacity top-2 left-2 p-4 group absolute duration-75 group-focus-within/card:opacity-100 group-hover/card:opacity-100 group-hover/card:delay-75  z-30 isolate pointer-events-auto "
          :class="[
            props.isSelected ? 'opacity-100' : 'opacity-0',
          ]"
          role="checkbox"
          :aria-checked="props.isSelected"
          :aria-label="`Select this ${props.eventGroup ? 'group' : 'event'} - ${props.eventGroup ? props.eventGroup.name : props.event.name}`"
        >
          <AM3Checkbox
            :id="props.event.oid"
            :data-test-id="dataTestId"
            :aria-label="`Select this ${props.eventGroup ? 'group' : 'event'} - ${props.eventGroup ? props.eventGroup.name : props.event.name}`"
            :model-value="props.isSelected"
            @update:model-value="handleSelectionChange"
          />
        </div>

        <div
          v-if="props.enableDropdown"
          class="top-2 left-2 pl-4 pr-5 py-[22px] group-focus-within/card:opacity-100 opacity-0 z-40 text-inverse isolate group-hover/card:opacity-100 group-hover/card:delay-75  duration-75 pointer-events-auto"
          @click.prevent
        >
          <AM3DropdownMenu
            :options="options"
            :get-value="getOptionValue"
            :get-label="getOptionLabel"
            icon-type="ellipsis-vertical"
            :aria-label="`More actions for ${props.eventGroup ? props.eventGroup.name : props.event.name}`"
            @change="handleOptionChange"
          />
        </div>

        <!-- group indicator -->
        <div
          v-if="event.type === 'event-group' || (event.count ?? 0) >= 1"
          class="flex flex-row items-center absolute left-6 z-30 group isolate px-2 py-1 rounded-full text-xs text-inverse bg-primary bg-opacity-50 bottom-4"
          role="status"
          :aria-label="`This is a group containing ${event.count} event${event.count !== 1 ? 's' : ''}`"
          data-test-id="event-group-indicator"
          @click="handleEventNavigation"
        >
          <AM3Icon
            type="calendar-multi"
            size="sm"
          />

          <span class="inline-block ml-1">{{ event.count ?? 0 }} event{{ (event.count ?? 0) !== 1 ? 's' : '' }}</span>
        </div>
      </div>

      <!-- Card Content -->
      <NuxtLink
        :to="{ path: eventLink }"
        class="block flex-1 text-inherit no-underline"
        :aria-label="`View details for ${props.eventGroup ? 'group' : 'event'}: ${props.eventGroup ? props.eventGroup.name : props.event.name}`"
        @click="handleEventNavigation"
      >
        <div class="flex flex-1 flex-col min-h-[9.75rem] bg-surface-light relative z-30 justify-between p-4 sm:py-4 sm:pb-6 sm:px-6">
          <div class="flex flex-col h-full text-sm text-secondary">

            <!-- Title -->
            <h3 class="mb-2 font-medium text-lg text-primary max-full truncate text-ellipsis">
              {{ props.eventGroup ? props.eventGroup.name : props.event.name }}
            </h3>

            <!-- Date Range -->
            <div
              v-tooltip.bottom="toolTip"
              class="mb-2 flex items-center max-full truncate text-ellipsis"
              role="group"
              aria-label="Event date"
            >
              <AM3Icon

                type="calendar"
                aria-hidden="true"
                class="min-w-[20px]"
              />
              <time
                :datetime="props.eventGroup ? '' : props.event.startDate"
                data-datetime="startDate"
                class="ml-2 whitespace-nowrap"
              >
                {{ startDate || "No start date - add an event" }}
              </time>
              <time
                v-if="endDate"
                :datetime="props.eventGroup ? '' : props.event.endDate"
                data-datetime="endDate"
                class="whitespace-nowrap truncate text-ellipsis"
              >
                &nbsp;- {{ endDate }}
              </time>
              <span
                v-if="timezone"
                class="ml-1 whitespace-nowrap"
              >
                ({{ timezone }})
              </span>
            </div>

            <!-- Time of first -->
            <div

              class="mb-2 flex items-center max-full truncate text-ellipsis"
              role="group"
              aria-label="Event time"
            >
              <AM3Icon
                type="clock"
                aria-hidden="true"
              />
              <span class="items-center inline-flex h-5">
                <time
                  data-datetime="startTime"
                  class="ml-2 whitespace-nowrap"
                >
                  {{ startTime || '-' }}
                </time>
                <span
                  v-if="endTime && startTime !== endTime"
                  class="mx-1"
                >
                  -
                </span>

                <time
                  v-if="endTime && startTime !== endTime"
                  data-datetime="endTime"
                  class="whitespace-nowrap truncate text-ellipsis"
                >
                  {{ endTime }}
                </time>
              </span>

            </div>

            <!-- Location -->
            <div

              class="flex items-center"
              role="group"
              aria-label="Event location"
            >
              <AM3Icon

                type="map-pin"
                aria-hidden="true"
              />
              <address

                class="ml-2 text-secondary truncate not-italic"
              >
                {{ location || '-' }}
              </address>
            </div>

            <!-- Ticket Stats -->
            <div
              class="mt-4 pt-5 border-t border-weak flex items-center justify-between text-sm"
              role="group"
              aria-label="Ticket statistics"
            >
              <div class="flex items-center gap-1">
                <AM3Icon
                  type="ticket"
                  size="sm"
                  aria-hidden="true"
                />
                <span class="font-medium">{{ (ticketsSold ?? 0).toLocaleString() }} tickets</span>
              </div>
              <div class="font-medium text-primary">
                {{ currencySymbol }}{{ formatCurrencyValueWithCommas(centsToDollars(ticketSales ?? 0)) }}
              </div>
            </div>

          </div>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { centsToDollars } from '@audience-republic/audience-manager-shared/utils/currency'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { usePermissions } from '@/composables/usePermissions'

import type { Event } from '@/types/resources/event'
import type { TEventGroup } from '@/components/am3/event-groups/AM3EventGroups.vue'
import { formatDateTime, formatTimezone, formatCurrencyValueWithCommas, resolveStartTimeZone, resolveEndTimeZone } from '@/utils/formatters'
import { getIntegrationIcon } from '@/utils/integrationIcons'
import AM3Checkbox from '@/components/am3-generic/inputs/selection/AM3Checkbox.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import { useCurrencySymbol } from '@/composables/useCurrencySymbol'

const { canViewSalesData } = usePermissions()

interface IntegrationData {
  name: string
  icon: string
}

interface Option {
  label: string
  action: string
  disabled: boolean
}

interface Resource {
  assetType: string
  url: string
}

interface Props {
  event: Event
  link?: string
  integration?: IntegrationData | null
  enableDropdown?: boolean
  selectable?: boolean
  isSelected?: boolean
  eventGroup?: TEventGroup | null
  showArchive?: boolean
  showUnarchive?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  link: '',
  integration: null,
  enableDropdown: true,
  selectable: false,
  isSelected: false,
  eventGroup: null,
  showArchive: true,
  showUnarchive: false,
})

const emit = defineEmits<{
  'edit': [id: string | number]
  'duplicate': [id: string | number]
  'archive': [id: string | number]
  'unarchive': [id: string | number]
  'delete': [id: string | number]
  'import-orders': [id: string | number]
  'clear-ticket-orders': [id: string | number]
  'add-to-groups': [id: string | number]
  'selection-change': [payload: {
    event: Event | null
    group: TEventGroup | null
    target: Event | TEventGroup
    selected: boolean
  }]
}>()

// Computed properties
const dataTestId = computed(() => {
  const isGroup = props.eventGroup || props.event?.type === 'event-group'
  const prefix = isGroup ? 'am3-group-card' : 'am3-event-card'
  const id = props.eventGroup ? props.eventGroup.id : props.event?.oid
  return generateDataTestId(prefix, id.toString())
})

const toolTip = computed(() => {
  if (endDate.value) {
    return `${startDate.value} - ${endDate.value} (${timezone.value}) `
  }
  return `${startDate.value} (${timezone.value}) `
})

// Helper: Check if this is an event group
const isEventGroup = computed(() => Boolean(props.eventGroup))

// Helper: Get events from group
const groupEvents = computed(() => props.eventGroup?.events || [])

// Helper: Check if event spans multiple days
const isMultiDayEvent = computed(() => {
  if (!props.event.endDate) return false
  const diffMs = new Date(props.event.endDate).getTime() - new Date(props.event.startDate).getTime()
  return diffMs >= 24 * 3600 * 1000
})

const resolvedStartTimeZone = computed(() => resolveStartTimeZone(props.event))
const resolvedEndTimeZone = computed(() => resolveEndTimeZone(props.event))

const startDate = computed(() => {
  if (isEventGroup.value) {
    if (groupEvents.value.length === 0) {
      return `${props.eventGroup.eventCount} events`
    }

    const firstEvent = [...groupEvents.value].sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    )[0]
    const lastEvent = [...groupEvents.value].sort(
      (a, b) => new Date(a.endDate || a.startDate).getTime() - new Date(b.endDate || b.startDate).getTime(),
    ).at(-1)

    if (groupEvents.value.length === 1) {
      return formatDateTime(firstEvent.startDate, { dateStyle: 'custom-date', timeZone: resolvedStartTimeZone.value }) || ''
    }

    const firstDate = formatDateTime(firstEvent.startDate, { dateStyle: 'custom-date', timeZone: resolvedStartTimeZone.value })
    const lastDate = formatDateTime(lastEvent.endDate || lastEvent.startDate, { dateStyle: 'custom-date', timeZone: resolvedStartTimeZone.value })
    return `${firstDate} - ${lastDate} (${props.eventGroup.eventCount} Events)`
  }

  return formatDateTime(props.event.startDate, { dateStyle: 'custom-date', timeZone: resolvedStartTimeZone.value }) || ''
})

const endDate = computed(() => {
  if (!props.event.endDate || !isMultiDayEvent.value) {
    return false
  }

  if (isEventGroup.value) {
    if (groupEvents.value.length === 0) {
      return `${props.eventGroup.eventCount} events`
    }

    const sortedByEndDate = [...groupEvents.value].sort(
      (a, b) => new Date(a.endDate || a.startDate).getTime() - new Date(b.endDate || b.startDate).getTime(),
    )
    const firstEvent = sortedByEndDate[0]
    const lastEvent = sortedByEndDate.at(-1)

    if (groupEvents.value.length === 1) {
      return formatDateTime(firstEvent.endDate, { dateStyle: 'custom-date', timeZone: resolvedEndTimeZone.value }) || ''
    }

    const firstDate = formatDateTime(firstEvent.endDate, { dateStyle: 'custom-date', timeZone: resolvedEndTimeZone.value })
    const lastDate = formatDateTime(lastEvent.endDate, { dateStyle: 'custom-date', timeZone: resolvedEndTimeZone.value })
    return `${firstDate} - ${lastDate} (${props.eventGroup.eventCount} Events)`
  }

  return formatDateTime(props.event.endDate, { dateStyle: 'custom-date', timeZone: resolvedEndTimeZone.value }) || ''
})

const startTime = computed(() => {
  if (isEventGroup.value) {
    if (groupEvents.value.length === 0) return 'Various times'

    const earliestEvent = [...groupEvents.value].sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    )[0]

    return formatDateTime(earliestEvent.startDate, {
      timeStyle: 'time',
      timeZone: resolvedStartTimeZone.value,
    }) || ''
  }

  if (props.event.derivedStartTime) {
    return props.event.derivedStartTime
  }

  return formatDateTime(props.event.startDate, {
    timeStyle: 'time',
    timeZone: resolvedStartTimeZone.value,
  }) || ''
})

const endTime = computed(() => {
  if (isEventGroup.value) {
    if (groupEvents.value.length === 0) return 'Various times'

    const latestEvent = [...groupEvents.value].sort(
      (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
    ).at(-1)

    return formatDateTime(latestEvent.endDate, {
      timeStyle: 'time',
      timeZone: resolvedEndTimeZone.value,
    }) || ''
  }

  if (props.event.derivedEndTime) {
    return props.event.derivedEndTime
  }

  return formatDateTime(props.event.endDate, {
    timeStyle: 'time',
    timeZone: resolvedEndTimeZone.value,
  }) || ''
})

const location = computed(() => {
  if (isEventGroup.value) {
    if (groupEvents.value.length === 0) return 'Multiple locations'

    const locations = [...new Set(groupEvents.value.map(e => e.location).filter(Boolean))]
    if (locations.length === 0) return ''
    if (locations.length === 1) return locations[0]
    return `${locations.length} locations`
  }

  return props.event.location || ''
})

const timezone = computed(() => {
  if (isEventGroup.value) return ''
  return formatTimezone(props.event.startDate, resolvedStartTimeZone.value)
})

const ticketsSold = computed(() => {
  if (!props.event.ticketStats || props.event.ticketStats.length === 0) return 0
  return props.event.ticketStats[0]?.totalTicketsSold ?? 0
})

const ticketSales = computed(() => {
  if (!props.event.ticketStats || props.event.ticketStats.length === 0) return 0
  return props.event.ticketStats[0]?.totalTicketSales ?? 0
})

const currencySymbol = useCurrencySymbol()

const integration = computed<IntegrationIcon | null>(() => {
  return props.event.provider ? getIntegrationIcon(props.event.provider) : null
})

const eventLink = computed(() => {
  if (props.link) return props.link

  // Check if this is an event-group type
  if (props.event.type === 'event-group') {
    return `/events/${props.event.oid}/view/group`
  }

  // Legacy eventGroup prop support
  if (props.eventGroup) {
    return `/events/${props.eventGroup.id}/view/group`
  }

  // Regular event — restricted users land on data-capture instead of ticket-sales
  const defaultTab = canViewSalesData.value ? '' : '#data-capture'
  return `/events/${props.event.oid}/view/event${defaultTab}`
})

// Handle navigation to store current path for return link
const handleEventNavigation = () => {
  if (typeof window !== 'undefined') {
    // Store the current path to session storage for return navigation
    const currentPath = window.location.pathname
    const currentHash = window.location.hash

    // Check for profile/audience paths first (most specific)
    // Profile events tab is at /audience/{id}/view#events
    if (currentPath.includes('/audience/') && currentPath.includes('/view')) {
      // Store the full path with hash if it's the events tab
      const fullPath = currentHash === '#events' ? `${currentPath}${currentHash}` : `${currentPath}#events`
      sessionStorage.setItem('eventsReturnPath', fullPath)
    }
    // Then check for events paths (more specific paths first)
    else if (currentPath.includes('/events/archived')) {
      sessionStorage.setItem('eventsReturnPath', '/events/archived')
    }
    else if (currentPath.includes('/events/past')) {
      sessionStorage.setItem('eventsReturnPath', '/events/past')
    }
    else if (currentPath.includes('/events')) {
      sessionStorage.setItem('eventsReturnPath', '/events')
    }
  }
}

const eventImage = computed(() => {
  // For event groups, we might not have resources, so just return null for now
  // This can be extended later if groups have their own images
  if (props.eventGroup) {
    return null
  }

  // For regular events, check resources
  if (!props.event.resources) return null

  const eventImage = props.event.resources.find(
    (resource: unknown): resource is Resource =>
      typeof resource === 'object'
      && resource !== null
      && 'assetType' in resource
      && (resource as Resource).assetType === 'event-image',
  )
  return eventImage?.url || null
})

const eventImagePlaceholder = '/placeholder-event.png'

// Options in dropdown, dependant on if it's a single/group
const options = computed(() => {
  const result = [] as Option[]

  // For archived events/groups, only show Unarchive and Delete
  if (props.showUnarchive) {
    result.push({ label: 'Unarchive', action: 'unarchive', disabled: false })
    result.push({ label: 'Delete', action: 'delete', disabled: false })
    return result
  }

  // If Single event
  if (props.event.type !== 'event-group') {
    result.push({ label: 'Edit event', action: 'edit', disabled: false })
    result.push({ label: 'Import ticket orders', action: 'import-orders', disabled: false })
    result.push({ label: 'Clear ticket orders', action: 'clear-ticket-orders', disabled: false })
    result.push({ label: 'Add to groups', action: 'add-to-groups', disabled: false })
    result.push({ label: 'Duplicate', action: 'duplicate', disabled: false })
  }

  // If it's a Group of Events
  else {
    result.push({ label: 'Edit event group', action: 'edit', disabled: false })
  }

  // Show Archive option for both events and groups (if not on archived page)
  if (props.showArchive) {
    result.push({ label: 'Archive', action: 'archive', disabled: false })
  }
  result.push({ label: 'Delete', action: 'delete', disabled: false })

  return result
})

// Methods
const getOptionValue = (option: Option): string => {
  return option.action
}

const getOptionLabel = (option: Option): string => {
  return option.label
}

const handleOptionChange = (action: string): void => {
  const targetId = props.eventGroup ? props.eventGroup.id : props.event.oid
  switch (action) {
    case 'edit':
      emit('edit', targetId)
      break
    case 'duplicate':
      emit('duplicate', targetId)
      break
    case 'import-orders':
      emit('import-orders', targetId)
      break
    case 'clear-ticket-orders':
      emit('clear-ticket-orders', targetId)
      break
    case 'add-to-groups':
      emit('add-to-groups', targetId)
      break
    case 'archive':
      emit('archive', targetId)
      break
    case 'unarchive':
      emit('unarchive', targetId)
      break
    case 'delete':
      emit('delete', targetId)
      break
  }
}

const handleSelectionChange = (selected: boolean): void => {
  const target = props.eventGroup || props.event
  emit('selection-change', {
    event: props.eventGroup ? null : props.event,
    group: props.eventGroup || null,
    target,
    selected,
  })
}
</script>
